// src/utils/planning/annualPlanEngine.js

import {
  getActivePlanningConfig,
  getPlanningVersions
} from "../../data/planning/planningConfig";

import {
  getPlanningRuleProfile
} from "../../data/planning/planningRules";

import {
  REGULAR_SCHOOL_CORE_PLAN
} from "../../data/planning/corePlanTemplate";

import {
  getOfficialActivityById
} from "../../data/planning/officialActivityCatalog";

import {
  getTargetActivityById
} from "../../data/planning/targetActivityCatalog";


import {
  getActivityTypeById
} from "../../data/planning/activityTypeCatalog";

import {
  getTbmRulesForSchoolLevel
} from "../../data/planning/tbmProgram";

/**
 * REHBER360 - YILLIK PLAN ÜRETİM MOTORU
 *
 * V1 görevi:
 *
 * Eğitim yılı
 * +
 * kural profili
 * +
 * okul kademesi
 * +
 * çekirdek plan şablonu
 * +
 * resmi faaliyet kataloğu
 *
 * ↓
 *
 * Standart yıllık plan taslağı
 *
 *
 * ÖNEMLİ:
 * - Bu motor faaliyet adı ÜRETMEZ.
 * - Yalnızca officialActivityCatalog içindeki
 *   faaliyetleri kullanabilir.
 * - Tarih dağıtımı burada yapılmaz.
 * - Tarihler daha sonra planScheduler.js
 *   tarafından atanacaktır.
 */


// =========================================================
// HANGİ PROFİL HANGİ ÇEKİRDEK ŞABLONU KULLANIR?
// =========================================================

const CORE_TEMPLATE_BY_PROFILE = {

  regular_school:
    REGULAR_SCHOOL_CORE_PLAN

};


/**
 * Şimdilik yalnızca normal okul profili için
 * çekirdek şablonumuzu tamamladık.
 *
 * Diğer kurum türleri için yanlış standartlar
 * üretmek yerine motor bilinçli olarak duracaktır.
 */


// =========================================================
// KADEME UYGUNLUK KONTROLÜ
// =========================================================

const isEntryApplicableToSchoolLevel = (
  entry,
  schoolLevel
) => {

  const applicableLevels =
    entry?.applicableSchoolLevels;

  // Kademe sınırlaması yoksa tüm normal
  // okullarda uygulanabilir.
  if (
    !Array.isArray(applicableLevels) ||
    applicableLevels.length === 0
  ) {
    return true;
  }

  return applicableLevels.includes(
    schoolLevel
  );
};


// =========================================================
// ÇEKİRDEK FAALİYET KAYNAĞINI ÇÖZ
// =========================================================
//
// Core plan artık iki doğrulanmış resmî katalogdan
// faaliyet kullanabilir:
//
// 1. officialActivityCatalog
//    Program yönetimi, toplantılar, RİBA,
//    örnek çerçeve plan standartları vb.
//
// 2. targetActivityCatalog
//    Öğrenci / veli / öğretmen hedef faaliyet havuzu.
//
// sourceCatalog belirtilmezse geriye dönük uyumluluk için
// officialActivityCatalog kullanılır.
// =========================================================

const resolveCoreActivity = (
  entry
) => {

  const sourceCatalog =
    entry?.sourceCatalog ||
    "officialActivityCatalog";


  if (
    sourceCatalog ===
    "officialActivityCatalog"
  ) {

    return {

      activity:
        getOfficialActivityById(
          entry?.activityId
        ),

      sourceCatalog,

      supported:
        true

    };
  }


  if (
    sourceCatalog ===
    "targetActivityCatalog"
  ) {

    return {

      activity:
        getTargetActivityById(
          entry?.activityId
        ),

      sourceCatalog,

      supported:
        true

    };
  }


  return {

    activity:
      null,

    sourceCatalog,

    supported:
      false

  };
};



// =========================================================
// ÇEKİRDEK ŞABLON BÜTÜNLÜK KONTROLÜ
// =========================================================

export const validateCoreTemplateIntegrity = ({
  profileId = "regular_school"
} = {}) => {

  const errors = [];

  const template =
    CORE_TEMPLATE_BY_PROFILE[
      profileId
    ];


  if (!template) {

    errors.push({
      code:
        "CORE_TEMPLATE_NOT_FOUND",

      message:
        `"${profileId}" için çekirdek plan şablonu henüz tanımlanmamış.`
    });

    return {
      valid: false,
      errors
    };
  }


  template.forEach(
    (entry, index) => {

      if (!entry?.activityId) {

        errors.push({
          code:
            "CORE_ACTIVITY_ID_MISSING",

          rowIndex: index,

          coreId:
            entry?.id || null,

          message:
            `${index + 1}. çekirdek plan kaydında activityId bulunmuyor.`
        });

        return;
      }


           const resolvedActivity =
        resolveCoreActivity(
          entry
        );


      if (
        !resolvedActivity.supported
      ) {

        errors.push({

          code:
            "CORE_ACTIVITY_SOURCE_NOT_SUPPORTED",

          rowIndex:
            index,

          coreId:
            entry.id,

          activityId:
            entry.activityId,

          sourceCatalog:
            resolvedActivity
              .sourceCatalog,

          message:
            `"${entry.id}" standart çalışmasında tanımsız faaliyet kaynağı kullanılıyor: "${resolvedActivity.sourceCatalog}".`

        });

        return;
      }


      if (
        !resolvedActivity.activity
      ) {

        errors.push({

          code:
            "CORE_ACTIVITY_NOT_OFFICIAL",

          rowIndex:
            index,

          coreId:
            entry.id,

          activityId:
            entry.activityId,

          sourceCatalog:
            resolvedActivity
              .sourceCatalog,

          message:
            `"${entry.activityId}" faaliyeti "${resolvedActivity.sourceCatalog}" içinde bulunmuyor.`

        });
      }
    }
  );


  return {

    valid:
      errors.length === 0,

    errors
  };
};


// =========================================================
// TEK BİR ÇEKİRDEK KAYDI PLAN SATIRINA DÖNÜŞTÜR
// =========================================================

const buildCorePlanRow = ({
  entry,
  officialActivity,
  academicYear,
  schoolLevel,
  index,
  activityTypeOverrides = {}
}) => {

  const timing =
    entry?.timing
      ? { ...entry.timing }
      : null;


   const allowedAudiences =
    Array.isArray(
      officialActivity.audience
    )
      ? [
          ...officialActivity.audience
        ]
      : officialActivity.audience
        ? [
            officialActivity.audience
          ]
        : [];


const defaultActivityType =
  entry?.activityTypeId
    ? getActivityTypeById(
        entry.activityTypeId
      )
    : null;


const requestedActivityType =
  entry?.id &&
  activityTypeOverrides?.[
    entry.id
  ]
    ? getActivityTypeById(
        activityTypeOverrides[
          entry.id
        ]
      )
    : null;


const hasValidActivityTypeOverride =
  Boolean(
    defaultActivityType &&
    requestedActivityType &&
    [1, 2, 3].includes(
      defaultActivityType.level
    ) &&
    requestedActivityType.level ===
      defaultActivityType.level
  );


const activityType =
  hasValidActivityTypeOverride
    ? requestedActivityType
    : defaultActivityType;


  return {

    // -----------------------------------------------------
    // REHBER360 SATIR KİMLİĞİ
    // -----------------------------------------------------

    id:
      `${academicYear}_${entry.id}`,

    coreId:
      entry.id,


    // -----------------------------------------------------
    // RESMÎ FAALİYET
    // -----------------------------------------------------

    activityId:
  officialActivity.id,

officialLabel:
  officialActivity.officialLabel,

contextLabel:
  entry.contextLabel ||
  null,

sourceType:
  "core",

sourceCatalog:
  entry.sourceCatalog ||
  "officialActivityCatalog",


mebbisCode:
  entry.mebbisCode ||
  null,

defaultActivityTypeId:
  defaultActivityType?.id ||
  null,

activityTypeId:
  activityType?.id ||
  null,

activityTypeLabel:
  activityType?.officialLabel ||
  null,

activityTypeShortLabel:
  activityType?.shortLabel ||
  null,

activityTypeSource:
  activityType
    ? (
        hasValidActivityTypeOverride
          ? "user_override"
          : "verified_core"
      )
    : null,

activityTypeOverridden:
  hasValidActivityTypeOverride,

    // -----------------------------------------------------
    // HEDEF BAĞLANTISI
    //
    // Standart çalışmalar genel / yerel /
    // özel hedef minimumlarına dahil değildir.
    // -----------------------------------------------------

     targetType: null,
    targetId: null,

    activityLevel:
      activityType?.level ??
      null,


    // -----------------------------------------------------
    // RESMÎ SUNUM SİSTEMİ SINIFLANDIRMASI
    // -----------------------------------------------------

    workMode:
      officialActivity.workMode ||
      null,

    serviceDomain:
      officialActivity.serviceDomain ||
      null,

    serviceType:
      officialActivity.serviceType ||
      null,


    // -----------------------------------------------------
    // HEDEF KİTLE
    // -----------------------------------------------------

    audience:
      allowedAudiences.length === 1
        ? allowedAudiences[0]
        : null,

    allowedAudiences,


    // -----------------------------------------------------
    // OKUL KADEMESİ
    // -----------------------------------------------------

      schoolLevel,

    /*
     * Bazı standart çalışmalar yalnız belirli
     * sınıf düzeylerinde uygulanır.
     *
     * Örnek:
     * - 8. sınıf
     * - 12. sınıf
     *
     * Boş dizi = kademe içindeki tüm sınıflar.
     */
    applicableGrades:
      Array.isArray(
        entry?.applicableGrades
      )
        ? Array.from(
            new Set(
              entry.applicableGrades
                .map(
                  grade =>
                    Number(grade)
                )
                .filter(
                  grade =>
                    Number.isInteger(
                      grade
                    ) &&
                    grade >= 1 &&
                    grade <= 12
                )
            )
          )
        : [],


    // -----------------------------------------------------
    // KULLANICI DAVRANIŞI
    // -----------------------------------------------------

    selectionMode:
      entry.selectionMode ||
      "default",

    locked:
      entry.selectionMode ===
      "locked",

    /**
     * default olanlar da plan ilk üretildiğinde
     * seçili gelir.
     *
     * Kullanıcı daha sonra:
     * - locked olanı kaldıramaz,
     * - default olanı kaldırabilir.
     */
    selected: true,


    // -----------------------------------------------------
    // TAKVİM
    // -----------------------------------------------------

    timingRule:
      timing,

    /**
     * Eğer çekirdek şablonda doğrudan ay
     * belirtilmişse ayı şimdiden taşıyoruz.
     *
     * school_start / balanced /
     * first_term_start gibi kuralların gerçek
     * tarihi scheduler tarafından bulunacak.
     */
    month:
      timing?.type === "month"
        ? timing.month
        : null,

    startDate: null,
    endDate: null,


    // -----------------------------------------------------
    // DURUM
    // -----------------------------------------------------

    status: "planned",

    order:
      entry.order ??
      ((index + 1) * 10)
  };
};


// =========================================================
// ÇEKİRDEK PLAN SATIRLARINI ÜRET
// =========================================================

export const generateCorePlanRows = ({
  academicYear,
  profileId = "regular_school",
  schoolLevel,
  activityTypeOverrides = {}
} = {}) => {

  const errors = [];
  const warnings = [];


  // -------------------------------------------------------
  // 1. EĞİTİM YILI
  // -------------------------------------------------------

  const config =
    getActivePlanningConfig(
      academicYear
    );


  if (!config) {

    errors.push({
      code:
        "PLANNING_CONFIG_NOT_FOUND",

      message:
        `${academicYear || "Seçilen eğitim yılı"} için aktif planlama konfigürasyonu bulunamadı.`
    });

    return {
      rows: [],
      errors,
      warnings
    };
  }


  // -------------------------------------------------------
  // 2. KURAL PROFİLİ
  // -------------------------------------------------------

  const profile =
    getPlanningRuleProfile(
      profileId
    );


  if (!profile) {

    errors.push({
      code:
        "PLANNING_PROFILE_NOT_FOUND",

      message:
        `"${profileId}" planlama profili bulunamadı.`
    });

    return {
      rows: [],
      errors,
      warnings
    };
  }


  // -------------------------------------------------------
  // 3. OKUL KADEMESİ
  // -------------------------------------------------------

  if (!schoolLevel) {

    errors.push({
      code:
        "SCHOOL_LEVEL_MISSING",

      message:
        "Plan oluşturmak için okul kademesi belirtilmelidir."
    });

    return {
      rows: [],
      errors,
      warnings
    };
  }


  /**
   * Profilin okul kademesi listesi varsa
   * seçilen kademeyle uyumlu olmalı.
   */
  if (
    Array.isArray(
      profile.applicableSchoolTypes
    ) &&
    profile.applicableSchoolTypes.length > 0 &&
    !profile.applicableSchoolTypes.includes(
      schoolLevel
    )
  ) {

    errors.push({
      code:
        "SCHOOL_LEVEL_PROFILE_MISMATCH",

      message:
        `"${schoolLevel}" okul kademesi "${profile.label}" profiliyle uyumlu değil.`
    });

    return {
      rows: [],
      errors,
      warnings
    };
  }


  // -------------------------------------------------------
  // 4. ÇEKİRDEK ŞABLON
  // -------------------------------------------------------

  const template =
    CORE_TEMPLATE_BY_PROFILE[
      profileId
    ];


  if (!template) {

    errors.push({
      code:
        "CORE_TEMPLATE_NOT_READY",

      message:
        `"${profile.label}" için otomatik çekirdek plan şablonu henüz hazır değil.`
    });

    return {
      rows: [],
      errors,
      warnings
    };
  }


  // -------------------------------------------------------
  // 5. ŞABLON BÜTÜNLÜĞÜ
  // -------------------------------------------------------

  const integrity =
    validateCoreTemplateIntegrity({
      profileId
    });


  if (!integrity.valid) {

    return {
      rows: [],
      errors: [
        ...errors,
        ...integrity.errors
      ],
      warnings
    };
  }


  // -------------------------------------------------------
  // 6. KADEMEYE UYGUN SATIRLARI SEÇ
  // -------------------------------------------------------

   const applicableEntries =
    template.filter(entry =>
      isEntryApplicableToSchoolLevel(
        entry,
        schoolLevel
      )
    );


  // -------------------------------------------------------
  // 7. TBM STANDART ÇALIŞMALARINI EKLE
  //
  // Kademe-modül eşleşmesi tbmProgram.js tarafından
  // belirlenir. Tarih "balanced" olarak scheduler'a bırakılır.
  // -------------------------------------------------------

  const tbmEntries =
    getTbmRulesForSchoolLevel(
      academicYear,
      schoolLevel
    ).map((rule, index) => ({

      id:
        `core_${rule.id}`,

      activityId:
        rule.activityId,

      activityTypeId:
        rule.activityTypeId ||
        null,

      mebbisCode:
        rule.mebbisCode ||
        null,

      timing:
        rule.timing
          ? { ...rule.timing }
          : {
              type: "balanced"
            },

      selectionMode:
        rule.selectionMode ||
        "locked",

      order:
        rule.order ??
        (
          1000 +
          ((index + 1) * 10)
        )
    }));


  const planEntries = [
    ...applicableEntries,
    ...tbmEntries
  ];


  // -------------------------------------------------------
  // 8. RESMÎ PLAN SATIRLARINI ÜRET
  // -------------------------------------------------------

  const rows =
    planEntries
      .map((entry, index) => {

               const resolvedActivity =
          resolveCoreActivity(
            entry
          );


        const officialActivity =
          resolvedActivity.activity;


        if (!officialActivity) {

          errors.push({

            code:
              "CORE_ACTIVITY_NOT_OFFICIAL",

            rowIndex:
              index,

            coreId:
              entry.id,

            activityId:
              entry.activityId,

            sourceCatalog:
              resolvedActivity
                .sourceCatalog,

            message:
              `"${entry.activityId}" faaliyeti "${resolvedActivity.sourceCatalog}" içinde bulunmuyor.`

          });

          return null;
        }


        return buildCorePlanRow({

          entry,

          officialActivity,

                   academicYear,

          schoolLevel,

          index,

          activityTypeOverrides
        });
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          a.order - b.order
      );


  return {

    rows,

    errors,

    warnings
  };
};


// =========================================================
// TAM YILLIK PLAN TASLAĞI OLUŞTUR
// =========================================================

export const generateAnnualPlanDraft = ({
  academicYear = "2026-2027",
  profileId = "regular_school",
  schoolLevel,
  selectedTargets = [],
  selectedActivityRows = [],
  excludedCoreIds = [],
  activityTypeOverrides = {}
} = {}) => {

  const config =
    getActivePlanningConfig(
      academicYear
    );


  if (!config) {

    return {

      success: false,

      plan: null,

      errors: [
        {
          code:
            "PLANNING_CONFIG_NOT_FOUND",

          message:
            `${academicYear} için aktif planlama konfigürasyonu bulunamadı.`
        }
      ],

      warnings: []
    };
  }


  const profile =
    getPlanningRuleProfile(
      profileId
    );


  if (!profile) {

    return {

      success: false,

      plan: null,

      errors: [
        {
          code:
            "PLANNING_PROFILE_NOT_FOUND",

          message:
            "Planlama kural profili bulunamadı."
        }
      ],

      warnings: []
    };
  }


  const coreResult =
    generateCorePlanRows({

      academicYear,

            profileId,

      schoolLevel,

      activityTypeOverrides
    });


  if (
    coreResult.errors.length > 0
  ) {

    return {

      success: false,

      plan: null,

      errors:
        coreResult.errors,

      warnings:
        coreResult.warnings
    };
  }


// -------------------------------------------------------
// STANDART PLAN SEÇİMİ
//
// locked satırlar kullanıcı tarafından hiçbir zaman
// çıkarılamaz.
// default satırlar ise kullanıcı tercihine göre
// nihai taslaktan çıkarılabilir.
// -------------------------------------------------------

const excludedCoreIdSet =
  new Set(
    Array.isArray(
      excludedCoreIds
    )
      ? excludedCoreIds
      : []
  );


const filteredCoreRows =
  coreResult.rows.filter(
    row => {

      if (row.locked) {
        return true;
      }

      return (
        !excludedCoreIdSet.has(
          row.coreId
        )
      );
    }
  );


  // -------------------------------------------------------
  // KULLANICININ SEÇTİĞİ HEDEFLER
  // -------------------------------------------------------

  const targetMap = {};

  selectedTargets.forEach(
    target => {

      if (
        !target?.type ||
        !target?.id
      ) {
        return;
      }

      targetMap[
        target.type
      ] = {
        id:
          target.id,

        label:
          target.label,

        developmentArea:
          target.developmentArea ||
          null
      };
    }
  );


  // -------------------------------------------------------
  // KULLANICI FAALİYETLERİNİ NORMALİZE ET
  //
  // Çekirdek faaliyetler önce işlenir.
  // Kullanıcı seçimleri onların ardından gelir.
  // -------------------------------------------------------

  const maxCoreOrder =
  filteredCoreRows.reduce(
      (maxOrder, row) =>
        Math.max(
          maxOrder,
          Number(
            row.order
          ) || 0
        ),
      0
    );


  const normalizedSelectedRows =
    (
      Array.isArray(
        selectedActivityRows
      )
        ? selectedActivityRows
        : []
    ).map(
      (row, index) => ({

        ...row,

        schoolLevel:
          row.schoolLevel ||
          schoolLevel,

        status:
          row.status ||
          "planned",

        month:
          row.month ??
          null,

        startDate:
          row.startDate ??
          null,

        endDate:
          row.endDate ??
          null,

        order:
          row.order ??
          (
            maxCoreOrder +
            ((index + 1) * 10)
          )
      })
    );


  const mergedActivities = [
  ...filteredCoreRows,
  ...normalizedSelectedRows
];


// -------------------------------------------------------
// SINIF / ŞUBE KAPSAMI
//
// all_classes:
//   Bu çalışma okulun tüm aktif sınıf/şubelerini kapsar.
//
// selected_classes:
//   Kullanıcı yalnızca belirli sınıf/şubeleri seçmiştir.
//
// not_applicable:
//   Komisyon, program hazırlama vb. sınıf/şube
//   kapsamı gerektirmeyen standart çalışmalardır.
//
// not_set:
//   Ek çalışmanın kapsamı henüz kullanıcı tarafından
//   belirlenmemiştir.
// -------------------------------------------------------

const activitiesWithCoverage =
  mergedActivities.map(row => {

    const usesClassCoverage =
      row.sourceType === "target" ||
      [
        "student",
        "parent",
        "teacher"
      ].includes(
        row.audience
      );


    if (usesClassCoverage) {

      return {
        ...row,

        coverage: {
          mode: "all_classes",
          classIds: [],
          displayLabel: "Tüm Sınıflar"
        }
      };
    }


    if (
      row.sourceType === "core"
    ) {

      return {
        ...row,

        coverage: {
          mode: "not_applicable",
          classIds: [],
          displayLabel: "—"
        }
      };
    }


    return {
      ...row,

      coverage: {
        mode: "not_set",
        classIds: [],
        displayLabel: "Belirlenmedi"
      }
    };
  });

  const plan = {

    // -----------------------------------------------------
    // PLAN BAĞLAMI
    // -----------------------------------------------------

    academicYear,

    profileId,

    profileLabel:
      profile.label,

    schoolLevel,


    // -----------------------------------------------------
    // HEDEFLER
    // -----------------------------------------------------

       targets: {

      general:
        targetMap.general
          ? {
              ...config.generalTarget,
              ...targetMap.general
            }
          : {
              ...config.generalTarget
            },

      local:
        targetMap.local ||
        null,

      special1:
        targetMap.special1 ||
        null,

      special2:
        targetMap.special2 ||
        null
    },


    // -----------------------------------------------------
    // BİRLEŞTİRİLMİŞ PLAN FAALİYETLERİ
    //
    // 1. Çekirdek / standart çalışmalar
    // 2. Kullanıcının seçtiği hedef faaliyetleri
    // 3. Kullanıcının seçtiği ek grup çalışmaları
    // -----------------------------------------------------

    activities:
  activitiesWithCoverage,

    activityCounts: {

      core:
  filteredCoreRows.length,

coreAvailable:
  coreResult.rows.length,

coreExcluded:
  coreResult.rows.length -
  filteredCoreRows.length,

      selected:
        normalizedSelectedRows.length,

      total:
        mergedActivities.length
    },


    // -----------------------------------------------------
    // REFERANS SÜRÜMLERİ
    // -----------------------------------------------------

    referenceVersions:
      getPlanningVersions(
        academicYear
      ),


    // -----------------------------------------------------
    // TASLAK DURUMU
    // -----------------------------------------------------

    status: "draft",

       generatedBy:
      "rehber360_plan_robot",

    generationMode:
      "manual_controlled",

    createdAt:
      Date.now()
  };


  return {

    success: true,

    plan,

    errors: [],

    warnings:
      coreResult.warnings
  };
};